import React, { useState } from 'react';
import { setTerminalAuth } from '../api/terminals';

const TYPES = [
  { value: 'none', label: 'None (public)' },
  { value: 'basic', label: 'Basic auth' },
  { value: 'apiKey', label: 'API key' },
  { value: 'jwt', label: 'JWT' },
];

export function AuthRulesPanel({ terminalId, auth, onChange }) {
  const [type, setType] = useState(auth?.type || 'none');
  const [username, setUsername] = useState(auth?.basic?.username || '');
  const [password, setPassword] = useState('');
  const [headerName, setHeaderName] = useState(auth?.apiKey?.headerName || 'x-api-key');
  const [jwtMode, setJwtMode] = useState(auth?.jwt?.mode || 'hs256');
  const [jwtSecret, setJwtSecret] = useState('');
  const [jwksUri, setJwksUri] = useState(auth?.jwt?.jwksUri || '');
  const [issuer, setIssuer] = useState(auth?.jwt?.issuer || '');
  const [audience, setAudience] = useState(auth?.jwt?.audience || '');
  const [busy, setBusy] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [error, setError] = useState(null);

  async function handleSave(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setGeneratedKey(null);
    try {
      let body;
      if (type === 'none') body = { type: 'none' };
      else if (type === 'basic') body = { type: 'basic', username, password };
      else if (type === 'apiKey') body = { type: 'apiKey', headerName };
      else body = { type: 'jwt', mode: jwtMode, secret: jwtSecret, jwksUri, issuer, audience };

      const updated = await setTerminalAuth(terminalId, body);
      onChange(updated.auth);
      if (updated.generatedApiKey) setGeneratedKey(updated.generatedApiKey);
      setPassword('');
      setJwtSecret('');
    } catch (err) {
      setError(err.response?.data?.error?.[0]?.message || err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel-section" onSubmit={handleSave}>
      <div className="radio-row">
        {TYPES.map((t) => (
          <label key={t.value} className="radio-item">
            <input type="radio" checked={type === t.value} onChange={() => setType(t.value)} />
            {t.label}
          </label>
        ))}
      </div>

      {type === 'basic' && (
        <div className="field-grid">
          <label>
            Username
            <input value={username} onChange={(e) => setUsername(e.target.value)} required />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={auth?.basic?.username ? '(unchanged - enter to reset)' : ''}
              required={!auth?.basic?.username}
            />
          </label>
        </div>
      )}

      {type === 'apiKey' && (
        <div className="field-grid">
          <label>
            Header name
            <input value={headerName} onChange={(e) => setHeaderName(e.target.value)} required />
          </label>
          {auth?.apiKey?.hasKey && (
            <p className="muted">A key is already set. Saving generates a new one and invalidates the old.</p>
          )}
        </div>
      )}

      {type === 'jwt' && (
        <div className="field-grid">
          <label>
            Mode
            <select value={jwtMode} onChange={(e) => setJwtMode(e.target.value)}>
              <option value="hs256">HS256 (shared secret)</option>
              <option value="jwks">RS256 (JWKS URL)</option>
            </select>
          </label>
          {jwtMode === 'hs256' ? (
            <label>
              Secret
              <input
                type="password"
                value={jwtSecret}
                onChange={(e) => setJwtSecret(e.target.value)}
                placeholder={auth?.jwt?.hasSecret ? '(unchanged - enter to reset)' : ''}
                required={!auth?.jwt?.hasSecret}
              />
            </label>
          ) : (
            <label>
              JWKS URI
              <input value={jwksUri} onChange={(e) => setJwksUri(e.target.value)} required />
            </label>
          )}
          <label>
            Issuer (optional)
            <input value={issuer} onChange={(e) => setIssuer(e.target.value)} />
          </label>
          <label>
            Audience (optional)
            <input value={audience} onChange={(e) => setAudience(e.target.value)} />
          </label>
        </div>
      )}

      {generatedKey && (
        <div className="banner banner-warning">
          Your API key (shown once, copy it now): <code>{generatedKey}</code>
        </div>
      )}
      {error && <div className="banner banner-error">{String(error)}</div>}

      <button className="btn btn-secondary" type="submit" disabled={busy}>
        Save auth rule
      </button>
    </form>
  );
}
